import type { CmChecklist } from '@luxuryescapes/contract-svc-offer';

import type { ChecklistDetail } from '../../shared/checklistUtils';
import { formatContextDate } from '../../shared/format';
import type { SurchargeCheckRowView, SurchargePeriodRowView } from '../../tabs/surcharges/types';
import type { WithChecklistItemMetadata } from '../../types';
import { presentRowBase } from '../rowBase';

const DEFAULT_SURCHARGE_CURRENCY = 'AUD';

export interface PresentedSurcharges {
  base: Array<WithChecklistItemMetadata<SurchargeCheckRowView>>;
  table: Array<WithChecklistItemMetadata<SurchargePeriodRowView>>;
}

export function presentSurchargesRows({
  checkRows,
  periodRows,
}: {
  checkRows: Array<CmChecklist.SurchargeCheckRow>;
  periodRows: Array<CmChecklist.SurchargePeriodRow>;
}): PresentedSurcharges {
  return {
    base: checkRows.map(presentSurchargeCheckRow),
    table: periodRows.map(presentSurchargePeriodRow),
  };
}

function presentSurchargeCheckRow(
  row: CmChecklist.SurchargeCheckRow,
): WithChecklistItemMetadata<SurchargeCheckRowView> {
  const base = presentRowBase(row);

  if (row.evidence.check === 'coverage') {
    return {
      ...base,
      details: presentCoverageDetails(row.evidence),
      field: 'Surcharge Coverage',
      name: 'Surcharge coverage rule',
    };
  }

  const overlappingPairs = presentOverlappingPairs(row.evidence, row.subject.surcharges);

  return {
    ...base,
    details: [{ label: 'Overlapping pairs', value: overlappingPairs.join('; ') || 'None' }],
    field: 'Overlapping Surcharge Dates',
    name: 'Overlapping surcharge dates',
    overlappingPairs,
  };
}

function presentCoverageDetails(evidence: CmChecklist.SurchargeCoverageEvidence): Array<ChecklistDetail> {
  const hasTravelPeriod = evidence.travel_days !== null;

  return [
    { label: 'Travel period days', value: hasTravelPeriod ? String(evidence.travel_days) : 'N/A' },
    { label: 'Surcharge days covered', value: hasTravelPeriod ? String(evidence.surcharge_days ?? 0) : 'N/A' },
    {
      label: 'Coverage',
      value: evidence.coverage_percent != null ? `${evidence.coverage_percent.toFixed(1)}%` : 'N/A',
    },
    { label: 'Travel from', value: formatContextDate(evidence.travel_from ?? undefined) },
    { label: 'Travel to', value: formatContextDate(evidence.travel_to ?? undefined) },
  ];
}

function presentOverlappingPairs(
  evidence: CmChecklist.SurchargeOverlapEvidence,
  surcharges: CmChecklist.SurchargeCheckRow['subject']['surcharges'],
) {
  const namesById = new Map(
    surcharges.map((surcharge) => [surcharge.surcharge_id, surcharge.name ?? surcharge.surcharge_id]),
  );

  return evidence.overlapping_pairs.map(
    ([first, second]) => `${namesById.get(first) ?? first} & ${namesById.get(second) ?? second}`,
  );
}

function presentSurchargePeriodRow(
  row: CmChecklist.SurchargePeriodRow,
): WithChecklistItemMetadata<SurchargePeriodRowView> {
  const base = presentRowBase(row);

  return {
    ...base,
    accumulate: row.evidence.accumulate,
    blackoutOverlap: row.evidence.blackout_overlap,
    blackoutOverlaps: row.evidence.blackout_overlaps.map((overlap, index) => ({
      from__c: overlap.from ?? '',
      name: row.subject.blackout_names[index] ?? '',
      to__c: overlap.to ?? '',
    })),
    convertedCost: row.subject.converted_cost ?? undefined,
    currencyCode: row.subject.currency_code || DEFAULT_SURCHARGE_CURRENCY,
    dealOptionNames: row.subject.deal_option_names,
    fromDate: row.evidence.from_date ?? '',
    insideTravelPeriod: row.evidence.inside_travel_period,
    name: row.subject.name ?? '',
    surchargePrice: row.subject.surcharge_price ?? undefined,
    toDate: row.evidence.to_date ?? '',
  };
}
